import { FC, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOktaAuth } from '@okta/okta-react'
import {
	Button,
	Container,
	Form,
	SpaceBetween,
	Textarea
} from '@cloudscape-design/components'
import useRejectCluster from '../../../hooks/mutate/useRejectCluster'
import type { Cluster } from '../../../types/cluster'
import type { ClusterRejectionRequest } from '../../../types/cluster'

interface ClusterRejectionFormProps {
	cluster: Cluster
	shouldShowRejectionForm: (show: boolean) => void
}

const ClusterRejectionForm: FC<ClusterRejectionFormProps> = ({
	cluster,
	shouldShowRejectionForm
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = (authState?.idToken?.claims?.email as string) ?? ''
	const queryClient = useQueryClient()
	const [commentText, setCommentText] = useState('')
	const clusterPk = cluster.workloadRepoNameEnv ?? cluster.clusterID
	const { mutate: rejectCluster, isLoading } = useRejectCluster(clusterPk)

	const handleReject = () => {
		const rejectionRequest: ClusterRejectionRequest = {
			clusterID: clusterPk,
			action: 'REJECT',
			commenterID: thisUserEmail,
			comment: commentText
		}
		rejectCluster(rejectionRequest, {
			onSuccess: () => {
				shouldShowRejectionForm(false)
				setCommentText('')
				queryClient.setQueryData(['cluster', clusterPk], {
					...cluster,
					status: 'REJECTED' as const
				})
			}
		})
	}

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" onClick={() => shouldShowRejectionForm(false)}>
						Cancel
					</Button>
					<Button variant="primary" loading={isLoading} onClick={handleReject}>
						Reject
					</Button>
				</SpaceBetween>
			}
		>
			<Container header="Reject cluster request">
				<SpaceBetween direction="vertical" size="l">
					<Textarea
						onChange={({ detail }) => setCommentText(detail.value)}
						value={commentText}
						placeholder="Reason for rejection"
					/>
				</SpaceBetween>
			</Container>
		</Form>
	)
}

export default ClusterRejectionForm
